import SignaturePad from "signature_pad"

/**
 * Profile letter-signature capture (Alpine.js component).
 *
 * Two routes to the same result — a trimmed, transparent PNG:
 *  - Draw: signature_pad on a canvas (transparent background already)
 *  - Upload: a scan/photo, cleaned here by knocking near-white pixels
 *    out to alpha so the paper background doesn't print as a grey box
 *
 * Both are cropped to the ink's bounding box before saving, so the
 * letter renderer can size the image consistently.
 */
export function signatureCapture(config) {
  return {
    mode: "idle", // idle | draw
    saving: false,
    error: null,
    pad: null,

    startDrawing() {
      this.mode = "draw"
      this.error = null
      this.$nextTick(() => {
        const canvas = this.$refs.canvas
        const ratio = Math.max(window.devicePixelRatio || 1, 1)
        const rect = canvas.getBoundingClientRect()
        canvas.width = rect.width * ratio
        canvas.height = 200 * ratio
        canvas.getContext("2d").scale(ratio, ratio)
        this.pad = new SignaturePad(canvas, {
          backgroundColor: "rgba(255,255,255,0)",
          penColor: "rgb(26,26,24)"
        })
      })
    },

    clearPad() {
      this.pad?.clear()
    },

    cancelDrawing() {
      this.pad?.off()
      this.pad = null
      this.mode = "idle"
    },

    async saveDrawn() {
      if (!this.pad || this.pad.isEmpty()) {
        this.error = "Draw your signature first."
        return
      }
      const processed = this.processCanvas(this.$refs.canvas, { knockoutWhite: false })
      if (processed) await this.submit(processed)
    },

    async uploadFile(event) {
      const file = event.target.files[0]
      event.target.value = ""
      if (!file) return
      this.error = null

      const image = new Image()
      const url = URL.createObjectURL(file)
      image.onload = () => {
        URL.revokeObjectURL(url)
        const canvas = document.createElement("canvas")
        // Cap the working size — a phone photo is far larger than needed.
        const scale = Math.min(1, 1600 / image.width)
        canvas.width = Math.round(image.width * scale)
        canvas.height = Math.round(image.height * scale)
        canvas.getContext("2d").drawImage(image, 0, 0, canvas.width, canvas.height)
        const processed = this.processCanvas(canvas, { knockoutWhite: true })
        if (processed) this.submit(processed)
      }
      image.onerror = () => {
        URL.revokeObjectURL(url)
        this.error = "That file couldn't be read as an image."
      }
      image.src = url
    },

    // Knocks near-white to transparent (uploads only) and crops to the
    // ink's bounding box with a little padding. Returns a PNG data URL,
    // or null if no ink was found.
    processCanvas(source, { knockoutWhite }) {
      const ctx = source.getContext("2d")
      const { width, height } = source
      const imageData = ctx.getImageData(0, 0, width, height)
      const px = imageData.data

      let minX = width, minY = height, maxX = -1, maxY = -1
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const i = (y * width + x) * 4
          if (knockoutWhite) {
            // Fade pixels toward transparent as they approach paper-white.
            const brightness = (px[i] + px[i + 1] + px[i + 2]) / 3
            if (brightness > 235) {
              px[i + 3] = 0
            } else if (brightness > 200) {
              px[i + 3] = Math.round(px[i + 3] * (235 - brightness) / 35)
            }
          }
          if (px[i + 3] > 20) {
            if (x < minX) minX = x
            if (x > maxX) maxX = x
            if (y < minY) minY = y
            if (y > maxY) maxY = y
          }
        }
      }

      if (maxX < 0) {
        this.error = "No signature found in that image."
        return null
      }

      const pad = 8
      minX = Math.max(0, minX - pad)
      minY = Math.max(0, minY - pad)
      maxX = Math.min(width - 1, maxX + pad)
      maxY = Math.min(height - 1, maxY + pad)

      const work = document.createElement("canvas")
      work.width = width
      work.height = height
      work.getContext("2d").putImageData(imageData, 0, 0)

      const out = document.createElement("canvas")
      out.width = maxX - minX + 1
      out.height = maxY - minY + 1
      out.getContext("2d").drawImage(work, -minX, -minY)
      return out.toDataURL("image/png")
    },

    async submit(dataUrl) {
      this.saving = true
      this.error = null
      try {
        const resp = await fetch(config.url, {
          method: "POST",
          headers: {
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
            "Content-Type": "application/json",
            "Accept": "application/json"
          },
          body: JSON.stringify({ signature_data: dataUrl })
        })
        const json = await resp.json()
        if (json.success) {
          window.Turbo ? Turbo.visit(window.location.href, { action: "replace" }) : window.location.reload()
        } else {
          this.error = json.error || "Couldn't save the signature."
        }
      } catch {
        this.error = "Couldn't save the signature."
      } finally {
        this.saving = false
      }
    }
  }
}
